import { diaNegocio, formatoHora12, hora12, sumarDias, type AsistenciaSemana, type RegistroDia, type TipoMarca } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Fingerprint } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tarjeta } from '@/components/ui/data';
import { Campo, Input, Select } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { cn, diaLargo } from '@/lib/utils';
import { avisarError } from '@/lib/avisos';

const h = (iso: string | null) => (iso ? hora12(new Date(iso)) : '—');
const ETIQUETA: Record<TipoMarca, string> = { llegada: 'Llegada', comida_inicio: 'Salida a comer', comida_fin: 'Regreso de comida', salida: 'Salida' };

function Celda({ r }: { r: RegistroDia }) {
  if (r.horario?.esDescanso && !r.llegada) return <span className="text-[12px] text-foreground-muted">Descanso</span>;
  if (r.permiso) return <Insignia tono="info">Permiso</Insignia>;
  if (r.falta) return <Insignia tono="peligro">Falta</Insignia>;
  if (!r.llegada) return <span className="text-[12px] text-foreground-muted">{r.horario ? `${formatoHora12(r.horario.entrada)} – ${formatoHora12(r.horario.salida)}` : '—'}</span>;
  return (
    <div className="cifras text-[12.5px] leading-tight">
      <p>
        {h(r.llegada)} → {h(r.salida)}
      </p>
      {r.comidaInicio && (
        <p className="text-foreground-muted">
          comida {h(r.comidaInicio)}–{h(r.comidaFin)}
        </p>
      )}
      <div className="mt-0.5 flex flex-wrap gap-1">
        {r.retardoMin > 0 && <Insignia tono="aviso">+{r.retardoMin} min tarde</Insignia>}
        {r.extraMin > 0 && <Insignia tono="info">{r.extraMin} min extra</Insignia>}
        {r.faltanteMin > 0 && <Insignia tono="peligro">−{r.faltanteMin} min</Insignia>}
      </div>
    </div>
  );
}

function siguienteMarca(r?: RegistroDia): TipoMarca | null {
  if (!r?.llegada) return 'llegada';
  if (r.salida) return null;
  if (r.comidaInicio && !r.comidaFin) return 'comida_fin';
  return r.comidaInicio ? 'salida' : 'comida_inicio';
}

export function AsistenciaPage() {
  const { usuario, puede } = useSesion();
  const qc = useQueryClient();
  const hoy = diaNegocio(new Date());
  const [dia, setDia] = useState(hoy);
  const [permiso, setPermiso] = useState(false);
  const [permisoDatos, setPermisoDatos] = useState({ usuarioId: '', dia: hoy, nota: '' });
  const q = useQuery({ queryKey: ['asistencia', dia], queryFn: () => api<AsistenciaSemana>('/asistencia', { query: { semana: dia } }) });

  const marcar = useMutation({
    mutationFn: ({ tipo, usuarioId }: { tipo: TipoMarca; usuarioId: string }) => api('/asistencia/marcar', { method: 'POST', body: { tipo, usuarioId } }),
    onSuccess: (_, v) => {
      toast.success(`${ETIQUETA[v.tipo]} registrada`);
      void qc.invalidateQueries({ queryKey: ['asistencia'] });
    },
    onError: (e) => avisarError(e),
  });
  const guardarPermiso = useMutation({
    mutationFn: () => api('/asistencia/permiso', { method: 'POST', body: permisoDatos }),
    onSuccess: () => {
      toast.success('Permiso registrado');
      setPermiso(false);
      void qc.invalidateQueries({ queryKey: ['asistencia'] });
    },
    onError: (e) => avisarError(e),
  });

  const d = q.data;
  return (
    <ModuleLayout titulo="Personal" secciones={SECCIONES_POR_MODULO.personal}>
      <EncabezadoPagina
        titulo="Asistencia"
        descripcion="Llegada, comida y salida comparadas con el horario: retardos, tiempo extra y tiempo de menos."
        acciones={
          puede('personal.horarios') && (
            <Button variante="secundario" onClick={() => setPermiso(true)}>
              Registrar permiso
            </Button>
          )
        }
      />
      <div className="mb-4 flex items-center gap-2">
        <Button variante="secundario" tamano="icono" onClick={() => setDia(sumarDias(d?.semana ?? dia, -7))} aria-label="Semana anterior">
          <ChevronLeft />
        </Button>
        <p className="min-w-56 text-center text-[14px] font-medium first-letter:uppercase">{d ? `Semana del ${diaLargo(d.semana)}` : '…'}</p>
        <Button variante="secundario" tamano="icono" onClick={() => setDia(sumarDias(d?.semana ?? dia, 7))} aria-label="Semana siguiente">
          <ChevronRight />
        </Button>
      </div>
      {!d ? (
        <Esqueleto className="h-64" />
      ) : (
        <Tarjeta className="overflow-x-auto">
          <table className="w-full min-w-[1000px] border-collapse">
            <thead>
              <tr>
                <th className="border-b border-border bg-surface-alt px-3 py-2 text-left text-[12px] font-semibold uppercase tracking-wide text-foreground-muted">Persona</th>
                {d.dias.map((x) => (
                  <th key={x} className={cn('border-b border-border bg-surface-alt px-2 py-2 text-left text-[12px] font-semibold text-foreground-muted', x === hoy && 'text-accent')}>
                    <span className="block uppercase tracking-wide">{diaLargo(x).split(',')[0]}</span>
                    <span className="cifras font-display text-[15px] text-foreground">{x.slice(8)}</span>
                  </th>
                ))}
                <th className="border-b border-border bg-surface-alt" />
              </tr>
            </thead>
            <tbody>
              {d.personas.map((p) => {
                const deHoy = p.registros.find((r) => r.dia === hoy);
                const proxima = siguienteMarca(deHoy);
                const puedeChecar = p.id === usuario?.id || puede('personal.checar_otros');
                return (
                  <tr key={p.id}>
                    <td className="border-b border-border px-3 py-2 text-[13.5px] font-medium">{p.nombre}</td>
                    {p.registros.map((r) => (
                      <td key={r.dia} className="border-b border-border px-2 py-2 align-top">
                        <Celda r={r} />
                      </td>
                    ))}
                    <td className="border-b border-border px-2 py-2 text-right">
                      {proxima && puedeChecar && d.dias.includes(hoy) && (
                        <Button tamano="sm" variante="secundario" onClick={() => marcar.mutate({ tipo: proxima, usuarioId: p.id })} cargando={marcar.isPending}>
                          <Fingerprint /> {ETIQUETA[proxima]}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Tarjeta>
      )}
      {permiso && d && (
        <Dialogo
          abierto
          ancho="sm"
          onCambiarAbierto={setPermiso}
          titulo="Registrar permiso"
          descripcion="El día no cuenta como falta."
          pie={
            <Button onClick={() => guardarPermiso.mutate()} cargando={guardarPermiso.isPending} disabled={!permisoDatos.usuarioId || !permisoDatos.nota}>
              Guardar
            </Button>
          }
        >
          <div className="grid gap-3">
            <Campo etiqueta="Persona">
              {(pp) => (
                <Select {...pp} value={permisoDatos.usuarioId} onChange={(e) => setPermisoDatos({ ...permisoDatos, usuarioId: e.target.value })}>
                  <option value="">Elige…</option>
                  {d.personas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>
            <Campo etiqueta="Día">{(pp) => <Input {...pp} type="date" value={permisoDatos.dia} onChange={(e) => setPermisoDatos({ ...permisoDatos, dia: e.target.value })} />}</Campo>
            <Campo etiqueta="Motivo">{(pp) => <Input {...pp} value={permisoDatos.nota} onChange={(e) => setPermisoDatos({ ...permisoDatos, nota: e.target.value })} />}</Campo>
          </div>
        </Dialogo>
      )}
    </ModuleLayout>
  );
}
